import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { 
  ArrowRight, Star, Shield, Play, Menu, X, BookOpen,
  Users, Receipt, Zap, BarChart3, FileText, Camera,
  Wallet, PieChart, Download, Sparkles, Heart, Euro,
  CheckCircle, Lock, RefreshCw, HelpCircle, ChevronDown
} from 'lucide-react';

// ============================================
// METEOR GRID COMPONENTS
// ============================================

function Meteor({ left, delay = 0, color = 'emerald' }) {
  const colorClasses = {
    emerald: {
      star: 'from-emerald-300/10 via-emerald-500 to-emerald-200 shadow-emerald-500/50',
      bg: 'from-emerald-500/60 via-emerald-300/10'
    },
    sky: {
      star: 'from-sky-300/10 via-sky-500 to-sky-200 shadow-sky-500/50',
      bg: 'from-sky-500/60 via-sky-300/10'
    },
    white: {
      star: 'from-white/10 via-white to-white/80 shadow-white/50',
      bg: 'from-white/60 via-white/10'
    },
    violet: {
      star: 'from-violet-300/10 via-violet-500 to-violet-200 shadow-violet-500/50',
      bg: 'from-violet-500/60 via-violet-300/10'
    },
    teal: {
      star: 'from-teal-300/10 via-teal-500 to-teal-200 shadow-teal-500/50',
      bg: 'from-teal-500/60 via-teal-300/10'
    }
  };

  const colors = colorClasses[color] || colorClasses.emerald;

  return (
    <div 
      className="absolute top-0 h-full"
      style={{ left: `${left}px` }}
    >
      {/* Meteor trail */}
      <motion.div
        className={`absolute w-[2px] h-20 bg-gradient-to-b ${colors.bg} to-transparent rounded-full blur-[1px]`}
        initial={{ top: -80, opacity: 0 }}
        animate={{ 
          top: ['calc(-80px)', 'calc(100% + 80px)'],
          opacity: [0, 1, 1, 0]
        }}
        transition={{
          duration: 2.5,
          delay: delay / 1000,
          repeat: Infinity,
          repeatDelay: 3,
          ease: "linear"
        }}
      />
      {/* Meteor head */}
      <motion.div
        className={`absolute w-[3px] h-[3px] bg-gradient-to-b ${colors.star} rounded-full shadow-lg`}
        initial={{ top: -4, opacity: 0 }}
        animate={{ 
          top: ['calc(-4px)', 'calc(100% + 4px)'],
          opacity: [0, 1, 1, 0]
        }}
        transition={{
          duration: 2.5,
          delay: delay / 1000,
          repeat: Infinity,
          repeatDelay: 3,
          ease: "linear"
        }}
      />
    </div>
  );
}

function MeteorGrid() {
  return (
    <div className="absolute inset-0 overflow-hidden">
      {/* Grid pattern */}
      <div 
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `
            linear-gradient(to right, #ffffff 1px, transparent 1px),
            linear-gradient(to bottom, #ffffff 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px'
        }}
      />
      
      {/* Meteors */}
      <div className="absolute inset-0" style={{ transform: 'skewY(-12deg)', transformOrigin: 'top left' }}>
        <Meteor left={60} delay={240} color="sky" />
        <Meteor left={180} delay={740} color="emerald" />
        <Meteor left={180} delay={0} color="teal" />
        <Meteor left={300} delay={1040} color="emerald" />
        <Meteor left={420} delay={1800} color="white" />
        <Meteor left={540} delay={860} color="emerald" />
        <Meteor left={660} delay={1440} color="teal" />
        <Meteor left={780} delay={3000} color="white" />
        <Meteor left={900} delay={2200} color="emerald" />
        <Meteor left={1020} delay={500} color="sky" />
        <Meteor left={1140} delay={1600} color="emerald" />
        <Meteor left={1260} delay={2800} color="teal" />
        <Meteor left={1380} delay={400} color="white" />
        <Meteor left={1500} delay={1200} color="emerald" />
        <Meteor left={1620} delay={2000} color="violet" />
        <Meteor left={1740} delay={600} color="emerald" />
        <Meteor left={1860} delay={1400} color="teal" />
      </div>
      
      {/* Gradient overlays */}
      <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/50" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-transparent to-black/80" />
    </div>
  );
}

// ============================================
// SECTION HERO - Black Theme with Meteor Grid
// ============================================

function HeroSection() {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-black">
      {/* Meteor Grid Background */}
      <MeteorGrid />
      
      {/* Radial gradient glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] bg-emerald-500/5 rounded-full blur-[120px]" />

      <div className="relative z-10 max-w-5xl mx-auto px-6 py-32">
        <div className="text-center">
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 mb-8 backdrop-blur-sm"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
            </span>
            <span className="text-sm font-medium text-emerald-400">
              Nouveau : OCR intelligent pour vos tickets
            </span>
            <ArrowRight className="h-3.5 w-3.5 text-emerald-500" />
          </motion.div>

          {/* Titre principal */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white mb-6"
          >
            Votre comptabilité
            <br />
            <span className="relative inline-block mt-2">
              <span className="bg-gradient-to-r from-emerald-400 via-emerald-300 to-teal-400 bg-clip-text text-transparent">
                en 5 minutes
              </span>
              <motion.div
                className="absolute -bottom-2 left-0 right-0 h-[3px] bg-gradient-to-r from-emerald-500 via-emerald-400 to-teal-500 rounded-full"
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 0.8, delay: 0.8 }}
              />
            </span>
          </motion.h1>

          {/* Sous-titre */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg sm:text-xl text-slate-400 max-w-2xl mx-auto mb-10 leading-relaxed"
          >
            L'application de comptabilité conçue pour les auto-entrepreneurs français.
            <span className="text-white font-medium"> Simple, conforme, sans prise de tête.</span>
          </motion.p>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16"
          >
            <Link to="/register">
              <Button 
                size="lg" 
                className="h-14 px-8 text-base font-semibold bg-emerald-500 hover:bg-emerald-400 text-black rounded-full shadow-xl shadow-emerald-500/25 hover:shadow-2xl hover:shadow-emerald-500/40 transition-all duration-300 group"
              >
                Commencer gratuitement
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <Button 
              variant="outline" 
              size="lg" 
              className="h-14 px-8 text-base font-semibold border-2 border-white/20 hover:border-white/40 bg-white/5 backdrop-blur-sm text-white hover:bg-white/10 rounded-full group"
            >
              <Play className="mr-2 h-4 w-4 text-emerald-400" />
              Voir la démo
            </Button>
          </motion.div>

          {/* Social proof */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-6 text-sm text-slate-500"
          >
            <div className="flex items-center gap-3">
              <div className="flex -space-x-2">
                {[1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-600 to-teal-600 border-2 border-black flex items-center justify-center text-xs font-medium text-white"
                  >
                    {String.fromCharCode(64 + i)}
                  </div>
                ))}
              </div>
              <span className="font-medium text-slate-300">+500 auto-entrepreneurs</span>
            </div>
            
            <div className="hidden sm:block w-px h-5 bg-slate-700" />
            
            <div className="flex items-center gap-1.5">
              {[1, 2, 3, 4, 5].map((i) => (
                <Star key={i} className="h-4 w-4 fill-emerald-400 text-emerald-400" />
              ))}
              <span className="ml-1 font-medium text-slate-300">4.9/5</span>
            </div>
            
            <div className="hidden sm:block w-px h-5 bg-slate-700" />
            
            <div className="flex items-center gap-1.5">
              <Shield className="h-4 w-4 text-emerald-500" />
              <span className="text-slate-400">Conforme Article 99 CGI</span>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="w-6 h-10 rounded-full border-2 border-slate-700 flex items-start justify-center p-2"
        >
          <div className="w-1 h-2 bg-emerald-500 rounded-full" />
        </motion.div>
      </motion.div>
    </section>
  );
}

// ============================================
// NAVBAR - Dark Theme
// ============================================

function Navbar({ scrolled, mobileMenuOpen, setMobileMenuOpen }) {
  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-black/80 backdrop-blur-xl border-b border-white/10'
          : 'bg-transparent'
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2.5">
          <div className="relative w-9 h-9">
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-xl rotate-6 opacity-60" />
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center">
              <BookOpen className="h-5 w-5 text-white" />
            </div>
          </div>
          <span className="font-bold text-xl tracking-tight">
            <span className="text-white">5min</span>
            <span className="text-emerald-400">Compta</span>
          </span>
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-8">
          <a href="#features" className="text-sm font-medium text-slate-400 hover:text-white transition-colors">
            Fonctionnalités
          </a>
          <a href="#pricing" className="text-sm font-medium text-slate-400 hover:text-white transition-colors">
            Tarifs
          </a>
          <a href="#testimonials" className="text-sm font-medium text-slate-400 hover:text-white transition-colors">
            Témoignages
          </a>
          <a href="#faq" className="text-sm font-medium text-slate-400 hover:text-white transition-colors">
            FAQ
          </a>
        </div>

        {/* CTA Buttons */}
        <div className="hidden md:flex items-center gap-3">
          <Link to="/login">
            <Button variant="ghost" className="text-sm font-medium text-slate-300 hover:text-white hover:bg-white/10">
              Se connecter
            </Button>
          </Link>
          <Link to="/register">
            <Button className="text-sm font-medium bg-emerald-500 hover:bg-emerald-400 text-black rounded-full px-5">
              Essai gratuit
            </Button>
          </Link>
        </div>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden p-2"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          {mobileMenuOpen ? (
            <X className="h-6 w-6 text-white" />
          ) : (
            <Menu className="h-6 w-6 text-white" />
          )}
        </button>
      </nav>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="md:hidden absolute top-16 left-0 right-0 bg-black/95 backdrop-blur-xl border-b border-white/10"
        >
          <div className="p-6 space-y-4">
            <a href="#features" className="block text-base font-medium text-white">Fonctionnalités</a>
            <a href="#pricing" className="block text-base font-medium text-white">Tarifs</a>
            <a href="#testimonials" className="block text-base font-medium text-white">Témoignages</a>
            <a href="#faq" className="block text-base font-medium text-white">FAQ</a>
            <div className="pt-4 border-t border-white/10 space-y-3">
              <Link to="/login" className="block">
                <Button variant="outline" className="w-full border-white/20 text-white hover:bg-white/10">Se connecter</Button>
              </Link>
              <Link to="/register" className="block">
                <Button className="w-full bg-emerald-500 hover:bg-emerald-400 text-black">Essai gratuit</Button>
              </Link>
            </div>
          </div>
        </motion.div>
      )}
    </motion.header>
  );
}

// ============================================
// SECTION COMPANY SHOWCASE - Trusted By / Partners
// ============================================

function CompanyShowcase() {
  const partners = [
    { name: 'URSSAF', letter: 'U' },
    { name: 'BPI France', letter: 'B' },
    { name: 'CCI France', letter: 'C' },
    { name: 'Banque de France', letter: 'BF' },
    { name: 'La French Tech', letter: 'FT' },
    { name: 'France Num', letter: 'FN' },
  ];

  return (
    <section className="relative py-20 bg-black border-y border-white/5 overflow-hidden">
      {/* Gradient overlays for fade effect */}
      <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-black to-transparent z-10" />
      <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-black to-transparent z-10" />

      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center text-sm font-medium text-slate-500 uppercase tracking-wider mb-10"
        >
          Ils nous font confiance
        </motion.p>

        {/* Scrolling logos - First row */}
        <div className="relative">
          <motion.div
            className="flex gap-12 items-center"
            animate={{ x: [0, -1200] }}
            transition={{
              x: {
                repeat: Infinity,
                repeatType: "loop",
                duration: 30,
                ease: "linear",
              },
            }}
          >
            {[...partners, ...partners, ...partners, ...partners].map((partner, i) => (
              <div
                key={i}
                className="flex-shrink-0 flex items-center gap-3 px-6 py-3 rounded-xl bg-white/[0.02] border border-white/5 hover:border-emerald-500/30 hover:bg-emerald-500/5 transition-all duration-300 group"
              >
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-slate-700 to-slate-800 flex items-center justify-center text-sm font-bold text-white group-hover:from-emerald-600 group-hover:to-emerald-700 transition-all">
                  {partner.letter}
                </div>
                <span className="text-slate-400 font-medium whitespace-nowrap group-hover:text-white transition-colors">
                  {partner.name}
                </span>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Stats row */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-16 pt-16 border-t border-white/5"
        >
          {[
            { value: '2 500+', label: 'Utilisateurs actifs', icon: Users },
            { value: '150K+', label: 'Transactions traitées', icon: Receipt },
            { value: '99.9%', label: 'Disponibilité', icon: Shield },
            { value: '< 1min', label: 'Temps de réponse', icon: Zap },
          ].map((stat, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 + i * 0.1 }}
              className="text-center group"
            >
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/20 mb-4 group-hover:scale-110 transition-transform">
                <stat.icon className="h-5 w-5 text-emerald-400" />
              </div>
              <p className="text-3xl font-bold text-white mb-1">{stat.value}</p>
              <p className="text-sm text-slate-500">{stat.label}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// ============================================
// SECTION FEATURES - Fonctionnalités détaillées
// ============================================

function FeaturesSection() {
  const features = [
    {
      icon: Receipt,
      title: 'Gestion des recettes & dépenses',
      description: 'Saisissez vos transactions en quelques clics. Catégorisation automatique et suivi mensuel.',
      color: 'emerald'
    },
    {
      icon: Camera,
      title: 'OCR intelligent',
      description: 'Photographiez vos tickets et factures. Notre IA extrait automatiquement les données.',
      color: 'blue'
    },
    {
      icon: FileText,
      title: 'Facturation intégrée',
      description: 'Créez devis et factures professionnels. Conversion automatique et suivi des paiements.',
      color: 'violet'
    },
    {
      icon: BarChart3,
      title: 'Tableau de bord temps réel',
      description: 'Visualisez votre activité avec des graphiques clairs. KPIs et tendances instantanés.',
      color: 'teal'
    },
    {
      icon: Wallet,
      title: 'Gestion de trésorerie',
      description: 'Prévisions sur 6 mois, alertes personnalisables et suivi des charges URSSAF.',
      color: 'orange'
    },
    {
      icon: Users,
      title: 'CRM simplifié',
      description: 'Gérez vos clients et fournisseurs. Historique des échanges et relances automatiques.',
      color: 'pink'
    },
    {
      icon: PieChart,
      title: 'Analyse de rentabilité',
      description: 'Marge par client, coût de revient par prestation. Optimisez votre activité.',
      color: 'cyan'
    },
    {
      icon: Download,
      title: 'Exports conformes',
      description: 'PDF et CSV pour l\'URSSAF et votre comptable. 100% conforme Article 99 CGI.',
      color: 'emerald'
    }
  ];

  return (
    <section id="features" className="relative py-32 bg-slate-950 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-emerald-500/5 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-blue-500/5 rounded-full blur-[150px]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm font-medium mb-4">
            <Sparkles className="h-3.5 w-3.5" />
            Fonctionnalités
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            Tout ce dont vous avez besoin
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Une suite complète d'outils conçus pour simplifier la vie des auto-entrepreneurs
          </p>
        </motion.div>

        {/* Features grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature, i) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="group relative bg-slate-900/50 backdrop-blur-sm border border-white/5 rounded-2xl p-6 hover:border-emerald-500/30 transition-all duration-300"
            >
              <div className={`w-12 h-12 rounded-xl bg-${feature.color}-500/10 border border-${feature.color}-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                <feature.icon className={`h-6 w-6 text-${feature.color}-400`} />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{feature.description}</p>
              
              {/* Hover effect */}
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-b from-emerald-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================
// SECTION TESTIMONIALS - Témoignages clients
// ============================================

function TestimonialsSection() {
  const testimonials = [
    {
      content: "5minCompta a transformé ma gestion comptable. Avant, je passais des heures chaque mois. Maintenant, c'est réglé en 10 minutes maximum !",
      author: "Marie Dupont",
      role: "Consultante freelance",
      rating: 5,
      avatar: "MD"
    },
    {
      content: "L'OCR est bluffant ! Je prends en photo mes tickets et tout est rempli automatiquement. Un vrai gain de temps au quotidien.",
      author: "Thomas Bernard",
      role: "Graphiste indépendant",
      rating: 5,
      avatar: "TB"
    },
    {
      content: "Enfin une application qui comprend les besoins des auto-entrepreneurs français. Les exports URSSAF sont parfaits.",
      author: "Sophie Martin",
      role: "Coach sportif",
      rating: 5,
      avatar: "SM"
    },
    {
      content: "Le tableau de bord est super clair. Je vois en un coup d'œil où j'en suis. La prévision de trésorerie m'a évité des surprises !",
      author: "Lucas Petit",
      role: "Développeur web",
      rating: 5,
      avatar: "LP"
    },
    {
      content: "Je recommande à tous mes clients auto-entrepreneurs. Simple, efficace et le support est très réactif.",
      author: "Claire Moreau",
      role: "Expert-comptable",
      rating: 5,
      avatar: "CM"
    },
    {
      content: "La facturation intégrée est un plus énorme. Mes devis se transforment en factures en un clic. Professionnel !",
      author: "Antoine Leroy",
      role: "Photographe",
      rating: 5,
      avatar: "AL"
    }
  ];

  return (
    <section id="testimonials" className="relative py-32 bg-black overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[600px] bg-emerald-500/5 rounded-full blur-[150px]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm font-medium mb-4">
            <Heart className="h-3.5 w-3.5" />
            Témoignages
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            Ils adorent 5minCompta
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Découvrez ce que nos utilisateurs disent de leur expérience
          </p>
        </motion.div>

        {/* Testimonials grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials.map((testimonial, i) => (
            <motion.div
              key={testimonial.author}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="bg-slate-900/50 backdrop-blur-sm border border-white/5 rounded-2xl p-6 hover:border-emerald-500/20 transition-all"
            >
              {/* Stars */}
              <div className="flex gap-1 mb-4">
                {[...Array(testimonial.rating)].map((_, j) => (
                  <Star key={j} className="h-4 w-4 fill-emerald-400 text-emerald-400" />
                ))}
              </div>
              
              {/* Content */}
              <p className="text-slate-300 mb-6 leading-relaxed">"{testimonial.content}"</p>
              
              {/* Author */}
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-sm font-bold text-white">
                  {testimonial.avatar}
                </div>
                <div>
                  <p className="font-medium text-white">{testimonial.author}</p>
                  <p className="text-sm text-slate-500">{testimonial.role}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================
// SECTION PRICING - Tarifs
// ============================================

function PricingSection() {
  const plans = [
    {
      name: 'Gratuit',
      price: '0',
      description: 'Pour démarrer en douceur',
      features: [
        'Jusqu\'à 20 transactions/mois',
        'Tableau de bord basique',
        'Export PDF',
        'Support par email'
      ],
      cta: 'Commencer gratuitement',
      popular: false
    },
    {
      name: 'Pro',
      price: '9',
      description: 'Pour les auto-entrepreneurs actifs',
      features: [
        'Transactions illimitées',
        'OCR tickets de caisse',
        'Facturation intégrée',
        'Gestion de trésorerie',
        'CRM clients',
        'Analyse de rentabilité',
        'Support prioritaire'
      ],
      cta: 'Essai gratuit 30 jours',
      popular: true
    },
    {
      name: 'Business',
      price: '19',
      description: 'Pour les pros exigeants',
      features: [
        'Tout le plan Pro',
        'Multi-activités',
        'API & intégrations',
        'Export comptable avancé',
        'Accompagnement personnalisé',
        'Support téléphonique'
      ],
      cta: 'Contacter les ventes',
      popular: false
    }
  ];

  return (
    <section id="pricing" className="relative py-32 bg-slate-950 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-emerald-500/5 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-violet-500/5 rounded-full blur-[150px]" />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm font-medium mb-4">
            <Euro className="h-3.5 w-3.5" />
            Tarifs simples
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            Choisissez votre plan
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Des tarifs transparents, sans surprise. Changez ou annulez à tout moment.
          </p>
        </motion.div>

        {/* Pricing cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {plans.map((plan, i) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.15 }}
              className={`relative rounded-2xl p-8 ${
                plan.popular 
                  ? 'bg-gradient-to-b from-emerald-500/10 to-slate-900/50 border-2 border-emerald-500/50' 
                  : 'bg-slate-900/50 border border-white/5'
              }`}
            >
              {/* Popular badge */}
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <span className="px-4 py-1 rounded-full bg-emerald-500 text-black text-sm font-semibold">
                    Plus populaire
                  </span>
                </div>
              )}

              {/* Plan info */}
              <div className="text-center mb-8">
                <h3 className="text-xl font-semibold text-white mb-2">{plan.name}</h3>
                <p className="text-sm text-slate-400 mb-4">{plan.description}</p>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-5xl font-bold text-white">{plan.price}</span>
                  <span className="text-slate-400">€/mois</span>
                </div>
              </div>

              {/* Features */}
              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, j) => (
                  <li key={j} className="flex items-center gap-3 text-sm text-slate-300">
                    <CheckCircle className="h-5 w-5 text-emerald-400 flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <Link to="/register">
                <Button 
                  className={`w-full h-12 font-semibold rounded-xl ${
                    plan.popular 
                      ? 'bg-emerald-500 hover:bg-emerald-400 text-black' 
                      : 'bg-white/10 hover:bg-white/20 text-white'
                  }`}
                >
                  {plan.cta}
                </Button>
              </Link>
            </motion.div>
          ))}
        </div>

        {/* Trust badges */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="flex flex-wrap items-center justify-center gap-8 mt-16 text-sm text-slate-500"
        >
          <div className="flex items-center gap-2">
            <Shield className="h-4 w-4 text-emerald-500" />
            Paiement sécurisé
          </div>
          <div className="flex items-center gap-2">
            <Lock className="h-4 w-4 text-emerald-500" />
            Données chiffrées
          </div>
          <div className="flex items-center gap-2">
            <RefreshCw className="h-4 w-4 text-emerald-500" />
            Annulation facile
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// ============================================
// SECTION FAQ - Questions fréquentes
// ============================================

function FAQSection() {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: "5minCompta est-il adapté aux auto-entrepreneurs ?",
      answer: "Absolument ! 5minCompta a été conçu spécifiquement pour les auto-entrepreneurs et micro-entrepreneurs français. L'interface est simple, les exports sont conformes aux exigences de l'URSSAF, et tout est pensé pour vous faire gagner du temps."
    },
    {
      question: "Comment fonctionne l'OCR pour les tickets ?",
      answer: "Notre technologie OCR utilise l'intelligence artificielle (Gemini Vision) pour analyser vos photos de tickets. Elle extrait automatiquement la date, le montant, le fournisseur et la nature de la dépense. Il vous suffit de vérifier et valider !"
    },
    {
      question: "Mes données sont-elles sécurisées ?",
      answer: "Oui, la sécurité est notre priorité. Vos données sont chiffrées en transit et au repos. Nous utilisons des serveurs sécurisés et respectons le RGPD. Vous pouvez exporter ou supprimer vos données à tout moment."
    },
    {
      question: "Puis-je essayer gratuitement ?",
      answer: "Oui ! Vous pouvez commencer avec notre plan gratuit (20 transactions/mois) ou essayer le plan Pro gratuitement pendant 30 jours, sans engagement et sans carte bancaire."
    },
    {
      question: "Les exports sont-ils conformes pour l'URSSAF ?",
      answer: "Oui, nos exports PDF respectent les exigences de l'Article 99 du Code Général des Impôts. Ils contiennent toutes les informations requises pour vos déclarations et sont acceptés par l'URSSAF."
    },
    {
      question: "Comment contacter le support ?",
      answer: "Notre équipe est disponible par email pour tous les utilisateurs. Les abonnés Pro bénéficient d'un support prioritaire avec réponse sous 24h. Les abonnés Business ont accès au support téléphonique."
    }
  ];

  return (
    <section id="faq" className="relative py-32 bg-black overflow-hidden">
      <div className="relative z-10 max-w-3xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm font-medium mb-4">
            <HelpCircle className="h-3.5 w-3.5" />
            FAQ
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            Questions fréquentes
          </h2>
          <p className="text-lg text-slate-400">
            Tout ce que vous devez savoir sur 5minCompta
          </p>
        </motion.div>

        {/* FAQ items */}
        <div className="space-y-4">
          {faqs.map((faq, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="border border-white/5 rounded-xl overflow-hidden"
            >
              <button
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                className="w-full flex items-center justify-between p-5 text-left bg-slate-900/50 hover:bg-slate-900/80 transition-colors"
              >
                <span className="font-medium text-white pr-4">{faq.question}</span>
                <ChevronDown 
                  className={`h-5 w-5 text-slate-400 flex-shrink-0 transition-transform ${
                    openIndex === i ? 'rotate-180' : ''
                  }`} 
                />
              </button>
              {openIndex === i && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="px-5 pb-5 bg-slate-900/30"
                >
                  <p className="text-slate-400 leading-relaxed">{faq.answer}</p>
                </motion.div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================
// SECTION CTA - Call to Action final
// ============================================

function CTASection() {
  return (
    <section className="relative py-32 bg-slate-950 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-500/10 via-transparent to-transparent" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-emerald-500/20 rounded-full blur-[150px]" />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            Prêt à simplifier votre comptabilité ?
          </h2>
          <p className="text-xl text-slate-400 mb-10 max-w-2xl mx-auto">
            Rejoignez plus de 2 500 auto-entrepreneurs qui gagnent du temps chaque semaine avec 5minCompta.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
            <Link to="/register">
              <Button 
                size="lg" 
                className="h-14 px-8 text-base font-semibold bg-emerald-500 hover:bg-emerald-400 text-black rounded-full shadow-xl shadow-emerald-500/25 group"
              >
                Démarrer gratuitement
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <Button 
              variant="outline" 
              size="lg" 
              className="h-14 px-8 text-base font-semibold border-2 border-white/20 hover:border-white/40 text-white hover:bg-white/10 rounded-full"
            >
              Voir la démo
            </Button>
          </div>

          <p className="text-sm text-slate-500">
            ✓ Essai gratuit 30 jours &nbsp;&nbsp; ✓ Sans carte bancaire &nbsp;&nbsp; ✓ Annulation en 1 clic
          </p>
        </motion.div>
      </div>
    </section>
  );
}

// ============================================
// SECTION FOOTER
// ============================================

function Footer() {
  return (
    <footer className="bg-black border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
          {/* Brand */}
          <div className="md:col-span-1">
            <Link to="/" className="flex items-center gap-2.5 mb-4">
              <div className="relative w-9 h-9">
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-xl rotate-6 opacity-60" />
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center">
                  <BookOpen className="h-5 w-5 text-white" />
                </div>
              </div>
              <span className="font-bold text-xl tracking-tight">
                <span className="text-white">5min</span>
                <span className="text-emerald-400">Compta</span>
              </span>
            </Link>
            <p className="text-sm text-slate-500 mb-4">
              La comptabilité simplifiée pour les auto-entrepreneurs français.
            </p>
            <div className="flex gap-4">
              <a href="#" className="text-slate-500 hover:text-white transition-colors">
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"/></svg>
              </a>
              <a href="#" className="text-slate-500 hover:text-white transition-colors">
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/></svg>
              </a>
            </div>
          </div>

          {/* Links */}
          <div>
            <h4 className="font-semibold text-white mb-4">Produit</h4>
            <ul className="space-y-3 text-sm">
              <li><a href="#features" className="text-slate-500 hover:text-white transition-colors">Fonctionnalités</a></li>
              <li><a href="#pricing" className="text-slate-500 hover:text-white transition-colors">Tarifs</a></li>
              <li><a href="#" className="text-slate-500 hover:text-white transition-colors">Mises à jour</a></li>
              <li><a href="#" className="text-slate-500 hover:text-white transition-colors">Roadmap</a></li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-white mb-4">Ressources</h4>
            <ul className="space-y-3 text-sm">
              <li><a href="#" className="text-slate-500 hover:text-white transition-colors">Blog</a></li>
              <li><a href="#" className="text-slate-500 hover:text-white transition-colors">Guides</a></li>
              <li><a href="#faq" className="text-slate-500 hover:text-white transition-colors">FAQ</a></li>
              <li><a href="#" className="text-slate-500 hover:text-white transition-colors">Support</a></li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-white mb-4">Légal</h4>
            <ul className="space-y-3 text-sm">
              <li><a href="#" className="text-slate-500 hover:text-white transition-colors">Mentions légales</a></li>
              <li><a href="#" className="text-slate-500 hover:text-white transition-colors">CGU</a></li>
              <li><a href="#" className="text-slate-500 hover:text-white transition-colors">Politique de confidentialité</a></li>
              <li><a href="#" className="text-slate-500 hover:text-white transition-colors">Cookies</a></li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="pt-8 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-slate-500">
            © 2026 5minCompta. Tous droits réservés.
          </p>
          <div className="flex items-center gap-6 text-sm text-slate-500">
            <span className="flex items-center gap-1.5">
              <Shield className="h-4 w-4 text-emerald-500" />
              Conforme RGPD
            </span>
            <span className="flex items-center gap-1.5">
              <Lock className="h-4 w-4 text-emerald-500" />
              SSL sécurisé
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}

// ============================================
// SECTION WORKFLOW - How It Works
// ============================================

function WorkflowSection() {
  const steps = [
    {
      number: '01',
      title: 'Créez votre compte',
      description: 'Inscription gratuite en 30 secondes. Aucune carte bancaire requise.',
      icon: Users,
      color: 'emerald'
    },
    {
      number: '02',
      title: 'Importez vos données',
      description: 'Scannez vos tickets avec l\'OCR intelligent ou saisissez manuellement.',
      icon: Receipt,
      color: 'teal'
    },
    {
      number: '03',
      title: 'Suivez en temps réel',
      description: 'Visualisez vos revenus, dépenses et trésorerie sur un tableau de bord clair.',
      icon: BarChart3,
      color: 'blue'
    },
    {
      number: '04',
      title: 'Exportez et déclarez',
      description: 'Générez vos rapports PDF conformes pour l\'URSSAF en un clic.',
      icon: FileText,
      color: 'violet'
    }
  ];

  return (
    <section id="workflow" className="relative py-32 bg-black overflow-hidden">
      {/* Background grid */}
      <div className="absolute inset-0 opacity-[0.02]">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }} />
      </div>

      {/* Glow effects */}
      <div className="absolute top-1/2 left-0 w-[600px] h-[600px] bg-emerald-500/10 rounded-full blur-[150px] -translate-y-1/2" />
      <div className="absolute top-1/2 right-0 w-[600px] h-[600px] bg-violet-500/10 rounded-full blur-[150px] -translate-y-1/2" />

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-slate-400 text-sm font-medium mb-4">
            <Zap className="h-3.5 w-3.5 text-emerald-400" />
            Simple & Rapide
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            Comment ça marche ?
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            4 étapes simples pour reprendre le contrôle de votre comptabilité
          </p>
        </motion.div>

        {/* Steps */}
        <div className="relative">
          {/* Connection line */}
          <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-y-1/2" />
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6">
            {steps.map((step, i) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
                className="relative group"
              >
                {/* Card */}
                <div className="relative bg-slate-900/50 backdrop-blur-sm border border-white/5 rounded-2xl p-6 hover:border-emerald-500/30 transition-all duration-500 h-full">
                  {/* Number badge */}
                  <div className="absolute -top-4 left-6">
                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-emerald-500 text-black text-sm font-bold">
                      {step.number}
                    </span>
                  </div>

                  {/* Icon */}
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br from-${step.color}-500/20 to-${step.color}-600/10 border border-${step.color}-500/20 flex items-center justify-center mb-5 mt-4 group-hover:scale-110 transition-transform duration-300`}>
                    <step.icon className={`h-7 w-7 text-${step.color}-400`} />
                  </div>

                  {/* Content */}
                  <h3 className="text-lg font-semibold text-white mb-2">
                    {step.title}
                  </h3>
                  <p className="text-sm text-slate-400 leading-relaxed">
                    {step.description}
                  </p>

                  {/* Hover glow */}
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-b from-emerald-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
                </div>

                {/* Arrow connector (hidden on mobile) */}
                {i < steps.length - 1 && (
                  <div className="hidden lg:flex absolute top-1/2 -right-3 w-6 h-6 items-center justify-center z-10">
                    <motion.div
                      initial={{ opacity: 0, x: -10 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.5 + i * 0.15 }}
                    >
                      <ArrowRight className="h-4 w-4 text-emerald-500" />
                    </motion.div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6 }}
          className="text-center mt-16"
        >
          <Link to="/register">
            <Button 
              size="lg" 
              className="h-14 px-8 text-base font-semibold bg-emerald-500 hover:bg-emerald-400 text-black rounded-full shadow-xl shadow-emerald-500/25 group"
            >
              Commencer maintenant
              <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
          <p className="mt-4 text-sm text-slate-500">
            Essai gratuit 30 jours • Sans engagement
          </p>
        </motion.div>
      </div>
    </section>
  );
}

// ============================================
// SECTION DEMO - Video/Interactive Preview
// ============================================

function DemoSection() {
  const [isPlaying, setIsPlaying] = useState(false);

  return (
    <section id="demo" className="relative py-32 bg-gradient-to-b from-black via-slate-950 to-slate-950 overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-teal-500/10 rounded-full blur-[120px]" />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm font-medium mb-4">
            <Play className="h-3.5 w-3.5" />
            Démo en 2 minutes
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            Découvrez la simplicité
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Voyez comment 5minCompta transforme la gestion comptable de milliers d'auto-entrepreneurs
          </p>
        </motion.div>

        {/* Video Player Mockup */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="relative"
        >
          {/* Glow effect */}
          <div className="absolute -inset-4 bg-gradient-to-r from-emerald-500/20 via-teal-500/20 to-emerald-500/20 rounded-3xl blur-2xl opacity-50" />
          
          {/* Video container */}
          <div className="relative bg-slate-900 rounded-2xl border border-white/10 overflow-hidden shadow-2xl shadow-black/50">
            {/* Browser bar */}
            <div className="flex items-center gap-2 px-4 py-3 bg-slate-800/50 border-b border-white/5">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full bg-red-500/80" />
                <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
                <div className="w-3 h-3 rounded-full bg-emerald-500/80" />
              </div>
              <div className="flex-1 mx-4">
                <div className="max-w-sm mx-auto bg-slate-700/50 rounded-md px-3 py-1 text-xs text-slate-400 text-center">
                  app.5mincompta.fr
                </div>
              </div>
            </div>

            {/* Video content area */}
            <div className="relative aspect-video bg-gradient-to-br from-slate-800 to-slate-900">
              {/* Dashboard preview content */}
              <div className="absolute inset-0 p-8">
                {/* Sidebar mock */}
                <div className="absolute left-0 top-0 bottom-0 w-56 bg-slate-800/80 border-r border-white/5 p-4">
                  <div className="flex items-center gap-2 mb-8">
                    <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                      <BookOpen className="h-4 w-4 text-emerald-400" />
                    </div>
                    <span className="font-semibold text-white text-sm">5minCompta</span>
                  </div>
                  {['Dashboard', 'Recettes', 'Dépenses', 'Factures', 'Clients'].map((item, i) => (
                    <motion.div
                      key={item}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.5 + i * 0.1 }}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg mb-1 text-sm ${i === 0 ? 'bg-emerald-500/20 text-emerald-400' : 'text-slate-400'}`}
                    >
                      {item}
                    </motion.div>
                  ))}
                </div>

                {/* Main content mock */}
                <div className="ml-60">
                  <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.8 }}
                  >
                    <h3 className="text-white font-semibold mb-4">Tableau de bord</h3>
                    
                    {/* KPI cards */}
                    <div className="grid grid-cols-3 gap-3 mb-4">
                      {[
                        { label: 'Recettes', value: '24 580 €', color: 'emerald' },
                        { label: 'Dépenses', value: '6 320 €', color: 'red' },
                        { label: 'Solde', value: '18 260 €', color: 'blue' },
                      ].map((stat, i) => (
                        <motion.div
                          key={stat.label}
                          initial={{ opacity: 0, scale: 0.9 }}
                          whileInView={{ opacity: 1, scale: 1 }}
                          viewport={{ once: true }}
                          transition={{ delay: 1 + i * 0.15 }}
                          className="bg-slate-700/50 rounded-xl p-3 border border-white/5"
                        >
                          <p className="text-xs text-slate-400 mb-1">{stat.label}</p>
                          <p className={`text-lg font-bold ${stat.color === 'emerald' ? 'text-emerald-400' : stat.color === 'red' ? 'text-red-400' : 'text-blue-400'}`}>
                            {stat.value}
                          </p>
                        </motion.div>
                      ))}
                    </div>

                    {/* Chart mock */}
                    <motion.div
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: 1.3 }}
                      className="bg-slate-700/30 rounded-xl p-4 border border-white/5"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium text-white">Évolution 2026</span>
                        <span className="text-xs text-emerald-400">+24%</span>
                      </div>
                      <div className="h-24 flex items-end gap-1">
                        {[30, 45, 35, 60, 50, 75, 65, 80, 70, 90, 85, 100].map((h, i) => (
                          <motion.div
                            key={i}
                            initial={{ height: 0 }}
                            whileInView={{ height: `${h}%` }}
                            viewport={{ once: true }}
                            transition={{ delay: 1.5 + i * 0.05, duration: 0.4 }}
                            className="flex-1 bg-gradient-to-t from-emerald-600 to-emerald-400 rounded-sm"
                          />
                        ))}
                      </div>
                    </motion.div>
                  </motion.div>
                </div>
              </div>

              {/* Play button overlay */}
              {!isPlaying && (
                <motion.button
                  initial={{ scale: 0.9, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.5 }}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setIsPlaying(true)}
                  className="absolute inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm group cursor-pointer"
                >
                  <div className="relative">
                    {/* Ripple effect */}
                    <div className="absolute inset-0 rounded-full bg-emerald-500/30 animate-ping" />
                    <div className="relative w-20 h-20 rounded-full bg-emerald-500 flex items-center justify-center shadow-lg shadow-emerald-500/30 group-hover:shadow-emerald-500/50 transition-shadow">
                      <Play className="h-8 w-8 text-black ml-1" fill="currentColor" />
                    </div>
                  </div>
                </motion.button>
              )}
            </div>

            {/* Video controls bar */}
            <div className="flex items-center gap-4 px-4 py-3 bg-slate-800/50 border-t border-white/5">
              <button className="text-slate-400 hover:text-white transition-colors">
                <Play className="h-4 w-4" />
              </button>
              <div className="flex-1 h-1 bg-slate-700 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: '0%' }}
                  whileInView={{ width: '35%' }}
                  viewport={{ once: true }}
                  transition={{ delay: 1, duration: 2 }}
                  className="h-full bg-emerald-500 rounded-full"
                />
              </div>
              <span className="text-xs text-slate-400">2:34</span>
            </div>
          </div>
        </motion.div>

        {/* Stats under video */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="grid grid-cols-3 gap-8 mt-16 max-w-3xl mx-auto"
        >
          {[
            { value: '5min', label: 'Temps moyen par semaine' },
            { value: '100%', label: 'Conforme réglementation' },
            { value: '4.9/5', label: 'Note utilisateurs' },
          ].map((stat, i) => (
            <div key={i} className="text-center">
              <motion.p
                initial={{ opacity: 0, scale: 0.5 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.6 + i * 0.1 }}
                className="text-3xl sm:text-4xl font-bold text-white mb-2"
              >
                {stat.value}
              </motion.p>
              <p className="text-sm text-slate-400">{stat.label}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// ============================================
// MAIN LANDING PAGE COMPONENT
// ============================================

function LandingPage() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-black">
      <Navbar 
        scrolled={scrolled} 
        mobileMenuOpen={mobileMenuOpen} 
        setMobileMenuOpen={setMobileMenuOpen} 
      />
      
      <main>
        <HeroSection />
        <DemoSection />
        <WorkflowSection />
        <FeaturesSection />
        <TestimonialsSection />
        <PricingSection />
        <FAQSection />
        <CTASection />
      </main>
      <Footer />
    </div>
  );
}

export default LandingPage;
